if (!customElements.get('product-reviews')) {
  customElements.define(
    'product-reviews',
    class ProductReviews extends HTMLElement {
      // Official Judge.me "Product Review Widget" endpoint (public API token).
      // Unlike GET /reviews it honours product groups, matching the
      // review_widget_data metafield page 1 is rendered from. It returns the
      // widget's HTML, which parseWidgetReviews turns back into review data.
      // Docs: https://judge.me/api (Main Widgets → Product Review Widget)
      static REVIEWS_URL = 'https://api.judge.me/api/v1/widgets/product_review';

      // Fallback: the JSON feed Judge.me's own storefront widget uses. Also
      // group-aware, but internal and undocumented (Judge.me support: "may
      // change or disappear without notice"), so it's only used when the
      // official endpoint fails or its HTML no longer parses into reviews.
      static FALLBACK_REVIEWS_URL = 'https://judge.me/reviews/reviews_for_widget';

      static STAR_ICON =
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">' +
        '<path d="M5.429 4.75509L7.20767 2.38576C7.31067 2.24843 7.42994 2.14915 7.5655 2.08793C7.70117 2.02671 7.84639 1.99609 8.00117 1.99609C8.15594 1.99609 8.30061 2.02671 8.43517 2.08793C8.56983 2.14915 8.68889 2.24843 8.79233 2.38576L10.571 4.75509L13.3115 5.68843C13.5329 5.7602 13.7031 5.88526 13.8218 6.06359C13.9406 6.24193 14 6.43887 14 6.65443C14 6.75398 13.985 6.85459 13.955 6.95626C13.925 7.05782 13.8763 7.14932 13.809 7.23076L12.0243 9.66059L12.091 12.2923C12.0996 12.5781 12.0038 12.8231 11.8038 13.0271C11.6037 13.2312 11.3645 13.3333 11.0862 13.3333C11.0903 13.3333 10.9936 13.3209 10.7962 13.2961L8 12.4679L5.20533 13.2953C5.14878 13.318 5.09589 13.33 5.04667 13.3313C4.99733 13.3326 4.95211 13.3333 4.911 13.3333C4.62756 13.3333 4.38694 13.2313 4.18917 13.0274C3.99128 12.8235 3.89789 12.5729 3.909 12.2756L3.97567 9.64693L2.17817 7.22693C2.11939 7.13515 2.075 7.03959 2.045 6.94026C2.015 6.84093 2 6.74159 2 6.64226C2 6.43082 2.06139 6.23376 2.18417 6.05109C2.30694 5.86832 2.47933 5.74315 2.70133 5.67559L5.429 4.75509Z" fill="#070707"/>' +
        '</svg>';

      static PER_PAGE = 5;

      connectedCallback() {
        this.reviewsContainer = this.querySelector('[data-reviews]');
        this.loadMoreButton = this.querySelector('[data-load-more]');

        this.productId = this.dataset.productId;
        this.shopDomain = this.dataset.shopDomain;
        this.apiToken = this.dataset.apiToken;
        this.totalReviews = Number(this.dataset.totalReviews) || 0;
        this.buyerLabel = this.dataset.buyerLabel || '';
        this.reviewedProductLabel = this.dataset.reviewedProductLabel || '';
        this.loading = false;

        // Lists are read in order, like Judge.me's own widget: with
        // multi-language sorting, the primary language first and then every
        // other language; otherwise one plain list. Each starts at page 1 and
        // skips what's already shown, because page 1 comes from the metafield,
        // whose language tags and freshness can differ from the live API.
        const language = this.dataset.primaryLanguage;
        const sourceParams = language ? [{ primary_language: language }, { exclude_language: language }] : [{}];
        this.sources = sourceParams.map((params) => ({ params, page: 1, done: false }));
        // Guards against an API that ignores `page` and never runs out.
        this.maxPagesPerSource = Math.ceil(this.totalReviews / this.constructor.PER_PAGE) + 1;
        this.queue = [];

        // Seeded with the server-rendered page 1 so load more never repeats one.
        this.seenReviewIds = new Set(
          [...this.querySelectorAll('[data-review-id]')].map((element) => element.dataset.reviewId).filter(Boolean),
        );
        this.renderedCount = this.querySelectorAll('.product-reviews__review').length;

        if (this.loadMoreButton) {
          this.loadMoreButton.addEventListener('click', () => this.loadMore());
        }
      }

      async loadMore() {
        if (this.loading) return;

        this.loading = true;
        this.loadMoreButton.disabled = true;

        try {
          await this.fillQueue();
          this.renderReviews(this.queue.splice(0, this.constructor.PER_PAGE));
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
          this.loadMoreButton.disabled = false;
          this.updateLoadMoreButton();
        }
      }

      // Collects at least one page of not-yet-shown reviews (extras wait in
      // the queue for the next click), moving to the next list when one ends.
      async fillQueue() {
        for (const source of this.sources) {
          while (!source.done && this.queue.length < this.constructor.PER_PAGE) {
            const reviews = await this.fetchReviews(source.page, source.params);
            source.page += 1;
            if (reviews.length < this.constructor.PER_PAGE || source.page > this.maxPagesPerSource) {
              source.done = true;
            }

            reviews.forEach((review) => {
              if (review.uuid && this.seenReviewIds.has(review.uuid)) return;
              if (review.uuid) this.seenReviewIds.add(review.uuid);
              this.queue.push(review);
            });
          }
        }
      }

      updateLoadMoreButton() {
        const exhausted = this.queue.length === 0 && this.sources.every((source) => source.done);
        if (exhausted || this.renderedCount >= this.totalReviews) {
          this.loadMoreButton.hidden = true;
        }
      }

      async fetchReviews(page, params) {
        try {
          const reviews = await this.fetchWidgetReviews(page, params);
          if (this.isCompleteParse(reviews)) return reviews;
          // Empty is also how a list ends, so only partial results warn; the
          // fallback then confirms the end or recovers the reviews.
          if (reviews.length > 0) {
            console.warn('Judge.me widget HTML parsed incompletely (markup changed?), using fallback feed');
          }
        } catch (error) {
          console.warn('Judge.me widget endpoint failed, using fallback feed', error);
        }

        return this.fetchFallbackReviews(page, params);
      }

      // An empty or partial result means the list ended or Judge.me changed
      // the widget markup. Every review has a name ("Anonymous" at minimum),
      // but rating-only reviews have no text, so text is only required
      // somewhere on the page.
      isCompleteParse(reviews) {
        return reviews.length > 0 && reviews.every((review) => review.uuid && review.rating > 0 && review.reviewer_name) && reviews.some((review) => review.body_html || review.title);
      }

      async fetchWidgetReviews(page, params = {}) {
        const url = new URL(this.constructor.REVIEWS_URL);
        url.searchParams.set('shop_domain', this.shopDomain);
        url.searchParams.set('api_token', this.apiToken);
        url.searchParams.set('external_id', this.productId);
        url.searchParams.set('per_page', this.constructor.PER_PAGE);
        url.searchParams.set('page', page);
        Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`Failed to load reviews: ${response.status}`);

        const data = await response.json();
        return this.parseWidgetReviews(data.widget || '');
      }

      // Already in the review_widget_data shape - no parsing needed.
      async fetchFallbackReviews(page, params = {}) {
        const url = new URL(this.constructor.FALLBACK_REVIEWS_URL);
        url.searchParams.set('url', this.shopDomain);
        url.searchParams.set('shop_domain', this.shopDomain);
        url.searchParams.set('platform', 'shopify');
        url.searchParams.set('product_id', this.productId);
        url.searchParams.set('per_page', this.constructor.PER_PAGE);
        url.searchParams.set('page', page);
        Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`Failed to load fallback reviews: ${response.status}`);

        const data = await response.json();
        return data.reviews || [];
      }

      // Maps each `.jdgm-rev` in the widget HTML to the same shape as the
      // review_widget_data metafield, so page 1 and later pages render alike.
      // DOMParser builds an inert document: no scripts run, no images load.
      parseWidgetReviews(widgetHtml) {
        const doc = new DOMParser().parseFromString(widgetHtml, 'text/html');
        const text = (element) => element?.textContent.trim() || '';

        return [...doc.querySelectorAll('.jdgm-rev')].map((element) => ({
          uuid: element.dataset.reviewId,
          reviewer_name: text(element.querySelector('.jdgm-rev__author')),
          verified_buyer: element.dataset.verifiedBuyer === 'true',
          created_at: element.querySelector('.jdgm-rev__timestamp')?.getAttribute('datetime') || '',
          rating: Number(element.querySelector('.jdgm-rev__rating')?.dataset.score) || 0,
          title: text(element.querySelector('.jdgm-rev__title')) || null,
          body_html: element.querySelector('.jdgm-rev__body')?.innerHTML.trim() || null,
          pictures_urls: [...element.querySelectorAll('.jdgm-rev__pic-img')].map((img) => ({ small: img.dataset.src || img.getAttribute('src') })).filter((picture) => picture.small),
          product_title: element.dataset.productTitle || '',
        }));
      }

      // Judge.me's product_title_localized is a locale -> title map (e.g. { de: '…' }), not a string:
      // take the page's language, otherwise the plain product title.
      getReviewedProductTitle(review) {
        const localized = review.product_title_localized;
        const localizedTitle = localized && typeof localized === 'object' ? localized[document.documentElement.lang] : localized;
        return localizedTitle || review.product_title;
      }

      renderReviews(reviews) {
        this.renderedCount += reviews.length;
        const html = reviews.map((review) => this.renderReview(review)).join('');
        this.reviewsContainer.insertAdjacentHTML('beforeend', html);
      }

      renderReview(review) {
        const author = review.reviewer_name;
        const verifiedBuyer = review.verified_buyer;
        const date = this.formatDate(review.created_at);
        const rating = review.rating;
        const title = review.title;
        const body = review.body_html;
        const picturesUrls = review.pictures_urls || [];
        const reviewedProductTitle = this.getReviewedProductTitle(review);

        return `
          <div class="product-reviews__review">
            <div class="product-reviews__review--header">
              <div>
                <span class="author">${this.escapeHtml(author)}</span>
                ${verifiedBuyer ? `<span class="buyer-label">${this.escapeHtml(this.buyerLabel)}</span>` : ''}
                ${
                  reviewedProductTitle && this.reviewedProductLabel
                    ? `<span class="reviewed-product">${this.escapeHtml(this.reviewedProductLabel.replace('[[product_title]]', () => reviewedProductTitle))}</span>`
                    : ''
                }
              </div>
              <span class="date">
                ${date}
              </span>
            </div>
            <div class="product-reviews__review--stars">
              ${this.renderStars(rating)}
            </div>
            <div class="product-reviews__review--body">
              ${
                title
                  ? `<span class="product-reviews__review--body-title">
                    ${this.escapeHtml(title)}
                  </span>`
                  : ''
              }
              ${body ? `<div class="product-reviews__review--body-inner">${body}</div>` : ''}
              ${
                picturesUrls.length > 0
                  ? `<div class="product-reviews__review--body-images">
                    ${picturesUrls.map((picture) => `<img src="${picture.small}" alt="review image" width="100" height="100" loading="lazy">`).join('')}
                  </div>`
                  : ''
              }
            </div>
          </div>
        `;
      }

      renderStars(rating) {
        return this.constructor.STAR_ICON.repeat(Number(rating) || 0);
      }

      formatDate(createdAt) {
        const date = new Date(createdAt);
        if (Number.isNaN(date.getTime())) return '';

        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const year = date.getUTCFullYear();
        return `${day}/${month}/${year}`;
      }

      escapeHtml(value) {
        const div = document.createElement('div');
        div.textContent = value ?? '';
        return div.innerHTML;
      }
    },
  );
}
